/**
 * Application routing and navigation guards.
 *
 * SECURITY POSTURE: these guards are a UX affordance, not a security boundary.
 * They stop an unauthenticated user from seeing an empty dashboard shell; they
 * are NOT what protects the data. Every API call is independently authenticated
 * and authorised server-side, so bypassing the router client-side yields nothing.
 *
 * Views are lazily imported so the login screen does not ship Vue Flow.
 */
import { watch } from 'vue';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useLoadingStore } from '@/stores/loading';
import { i18n } from '@/i18n';

// Titles are i18n keys (under `titles.`), resolved at display time so the
// document title and the shell header follow the active language.
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, titleKey: 'titles.login' },
  },
  {
    path: '/',
    component: () => import('@/components/AppShell.vue'),
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
        meta: { titleKey: 'titles.dashboard' },
      },
      {
        path: 'explorer',
        name: 'explorer',
        component: () => import('@/views/ExplorerView.vue'),
        meta: { titleKey: 'titles.explorer' },
      },
      {
        path: 'run',
        name: 'run',
        component: () => import('@/views/RunView.vue'),
        meta: { titleKey: 'titles.run' },
      },
      {
        path: 'runs',
        name: 'runs',
        component: () => import('@/views/RunsView.vue'),
        meta: { titleKey: 'titles.runs' },
      },
      {
        path: 'runs/:id',
        name: 'run-results',
        component: () => import('@/views/ResultsView.vue'),
        meta: { titleKey: 'titles.results' },
      },
      {
        path: 'shared',
        name: 'shared',
        component: () => import('@/views/SharedDataView.vue'),
        meta: { titleKey: 'titles.shared' },
      },
      {
        path: 'insights',
        name: 'insights',
        component: () => import('@/views/InsightsView.vue'),
        meta: { titleKey: 'titles.insights' },
      },
      {
        path: 'neurocloud',
        name: 'neurocloud',
        component: () => import('@/views/NeuroCloudView.vue'),
        meta: { titleKey: 'titles.neurocloud' },
      },
      {
        path: 'notes',
        name: 'notes',
        component: () => import('@/views/NotesView.vue'),
        meta: { titleKey: 'titles.notes' },
      },
      {
        path: 'admin/batches',
        name: 'admin',
        component: () => import('@/views/AdminBatchesView.vue'),
        meta: { titleKey: 'titles.admin' },
        // UX only (see the file comment): the backend 403s non-admins anyway.
        beforeEnter: async () => {
          const auth = useAuthStore();
          if (!auth.superAdminChecked) await auth.refreshSuperAdmin();
          return auth.superAdmin ? true : { name: 'dashboard' };
        },
      },
    ],
  },
  // Unknown paths go to the dashboard rather than a dead end; the guard below
  // will redirect to login if there is no session.
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
});

// The global overlay covers every navigation: lazy chunk downloads, the
// async guards below, and (via the same store) each view's first data fetch.
// A boolean rather than begin/end pairs, because a redirecting guard aborts
// the navigation and starts a new one — afterEach then fires only once.
router.beforeEach(() => {
  useLoadingStore().routeStarted();
});
router.afterEach(() => {
  useLoadingStore().routeSettled();
});
router.onError(() => {
  useLoadingStore().routeSettled();
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // On a hard refresh the session is read asynchronously. Without waiting, the
  // first navigation would see `isAuthenticated === false` and bounce a
  // legitimately signed-in user to the login page.
  if (auth.initialising) {
    await auth.initialise();
  }

  if (to.meta.public) {
    // A signed-in user has no reason to see the login form.
    if (to.name === 'login' && auth.isAuthenticated) return { name: 'dashboard' };
    return true;
  }

  if (!auth.isAuthenticated) {
    // Preserve the intended destination so sign-in can return the user there.
    return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } };
  }

  return true;
});

function applyDocumentTitle(): void {
  const key = (router.currentRoute.value.meta.titleKey as string | undefined) ?? 'titles.fallback';
  document.title = `${i18n.global.t(key)} · EEG Research Platform`;
}

router.afterEach(applyDocumentTitle);

// The tab title must also follow a language switch, not only navigations.
watch(i18n.global.locale, applyDocumentTitle);

export default router;
